/**
 * The GA4 event taxonomy for the Partner console — the single source of truth
 * for event names + their params. Keep names snake_case and STABLE: renaming an
 * event splits its history in GA4. New events get a constant here + a typed
 * helper below so call sites never pass a raw string.
 *
 * Docs: `docs/analytics-event-taxonomy.md` (what each event means + where it fires).
 */

import { logAnalyticsEvent, setAnalyticsUser, setAnalyticsUserProperties } from './analytics';
import { getAttribution } from './attribution';
import type { User } from '@/auth/types';

/** GA4 event names. `login`/`sign_up` are GA4 recommended events; rest are custom. */
export const AnalyticsEvent = {
  Login: 'login',
  SignUp: 'sign_up',
  Logout: 'logout',
  /** A visitor arrived on a marketing campaign link (utm_campaign present). */
  CampaignLanding: 'campaign_landing',
  OnboardingStart: 'onboarding_start',
  OnboardingStep: 'onboarding_step',
  OnboardingComplete: 'onboarding_complete',
  OnboardingFailed: 'onboarding_failed',
  ServiceActivate: 'service_activate',
  DomainAdd: 'domain_add',
  WafEnable: 'waf_enable',

  // Catalogue interest funnel (view → CTA → lead/waitlist)
  ViewItem: 'view_item',
  SelectItem: 'select_item',
  JoinWaitlist: 'join_waitlist',
  GenerateLead: 'generate_lead',

  // Monetization (GA4 ecommerce)
  BeginCheckout: 'begin_checkout',
  AddPaymentInfo: 'add_payment_info',
  AddPaymentMethod: 'add_payment_method',
  Purchase: 'purchase',

  // Engagement
  Search: 'search',
  SupportTicketCreate: 'support_ticket_create',
  AskAiOpen: 'ask_ai_open',

  /** Generic service-console feature interaction (create/edit/delete/enable…). */
  FeatureAction: 'feature_action',
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

// ---- Identity -------------------------------------------------------------

/** GA4 user_id + coarse, non-PII user properties (no email/name). */
export function identifyUser(user: User): void {
  void setAnalyticsUser(user.uuid, {
    org_id: user.organizationId ?? '',
    role: user.roles?.[0] ?? '',
  });
}

/** Clear the GA4 user id on logout. */
export function clearUser(): void {
  void setAnalyticsUser(null);
}

/**
 * Mirror the captured first-touch attribution into GA4 user properties so every
 * downstream event is segmentable by campaign. Call once after init.
 */
export function applyAttributionUserProps(): void {
  const a = getAttribution();
  if (!a) return;
  const props: Record<string, string> = {};
  if (a.utm_source) props.utm_source = a.utm_source;
  if (a.utm_campaign) props.utm_campaign = a.utm_campaign;
  if (a.utm_medium) props.utm_medium = a.utm_medium;
  if (a.promo_code) props.promo_code = a.promo_code;
  if (Object.keys(props).length) void setAnalyticsUserProperties(props);
}

// ---- Auth -----------------------------------------------------------------

export function trackLogin(method: string): void {
  void logAnalyticsEvent(AnalyticsEvent.Login, { method });
}

export function trackSignUp(method: string): void {
  void logAnalyticsEvent(AnalyticsEvent.SignUp, { method });
}

export function trackLogout(): void {
  void logAnalyticsEvent(AnalyticsEvent.Logout);
  clearUser();
}

// ---- Campaign / onboarding funnel -----------------------------------------

export function trackCampaignLanding(campaign: string): void {
  const a = getAttribution();
  void logAnalyticsEvent(AnalyticsEvent.CampaignLanding, {
    campaign,
    source: a?.utm_source,
    medium: a?.utm_medium,
  });
}

/** `source` = how onboarding was entered (e.g. "campaign_redirect", "cta", "nav"). */
export function trackOnboardingStart(source: string, campaign?: string): void {
  void logAnalyticsEvent(AnalyticsEvent.OnboardingStart, { source, campaign });
}

export function trackOnboardingStep(step: number, name: string): void {
  void logAnalyticsEvent(AnalyticsEvent.OnboardingStep, { step, step_name: name });
}

export function trackOnboardingComplete(domain?: string): void {
  void logAnalyticsEvent(AnalyticsEvent.OnboardingComplete, { domain });
}

export function trackOnboardingFailed(step: number | string, message?: string): void {
  void logAnalyticsEvent(AnalyticsEvent.OnboardingFailed, { step, message });
}

// ---- Service lifecycle ----------------------------------------------------

/** provider = the service codename (e.g. "raven"); category = human group. */
export function trackServiceActivate(provider: string, category?: string): void {
  void logAnalyticsEvent(AnalyticsEvent.ServiceActivate, { provider, category });
}

export function trackDomainAdd(serviceType: string, domain?: string): void {
  void logAnalyticsEvent(AnalyticsEvent.DomainAdd, { service_type: serviceType, domain });
}

export function trackWafEnable(domainId?: string): void {
  void logAnalyticsEvent(AnalyticsEvent.WafEnable, { domain_id: domainId });
}

// ---- Catalogue interest (which services customers care about) --------------

/** A catalog product detail page was viewed (passive interest). */
export function trackCatalogView(name: string, scope: string, category: string, status: string): void {
  void logAnalyticsEvent(AnalyticsEvent.ViewItem, {
    item_name: name,
    item_id: scope,
    item_category: category,
    status,
  });
}

/** The catalog CTA was clicked. `cta` = 'activate' | 'waitlist' | 'sales' (active interest). */
export function trackCatalogCta(name: string, scope: string, cta: string): void {
  void logAnalyticsEvent(AnalyticsEvent.SelectItem, { item_name: name, item_id: scope, cta });
}

/** Customer submitted a "join the waitlist" request for a coming-soon product. */
export function trackJoinWaitlist(name: string, scope: string): void {
  void logAnalyticsEvent(AnalyticsEvent.JoinWaitlist, { item_name: name, item_id: scope });
}

/** Customer submitted a sales inquiry for an available product. */
export function trackContactSales(name: string, scope: string): void {
  void logAnalyticsEvent(AnalyticsEvent.GenerateLead, { item_name: name, item_id: scope, lead_type: 'sales' });
}

/** Campaign lead form submitted (utm_campaign=waf_free register flow). */
export function trackWafFreeLead(solutionCount: number): void {
  void logAnalyticsEvent(AnalyticsEvent.GenerateLead, { lead_type: 'waf_free', solutions: solutionCount });
}

// ---- Monetization ---------------------------------------------------------

/** Payment dialog opened for an invoice. `value` is VND. */
export function trackBeginCheckout(invoiceId: string, amountVnd: number): void {
  void logAnalyticsEvent(AnalyticsEvent.BeginCheckout, {
    transaction_id: invoiceId,
    value: amountVnd,
    currency: 'VND',
  });
}

/** A payment method was chosen / initiated. `paymentType` = 'bank'|'momo'|'paypal'|'card'. */
export function trackAddPaymentInfo(paymentType: string, invoiceId?: string): void {
  void logAnalyticsEvent(AnalyticsEvent.AddPaymentInfo, { payment_type: paymentType, transaction_id: invoiceId });
}

/** A saved payment method (card) was added. */
export function trackAddPaymentMethod(paymentType = 'card'): void {
  void logAnalyticsEvent(AnalyticsEvent.AddPaymentMethod, { payment_type: paymentType });
}

/** A payment completed (FE-observable success: PayPal capture / card charge). `value` VND. */
export function trackPurchase(transactionId: string, value: number, paymentType: string, currency = 'VND'): void {
  void logAnalyticsEvent(AnalyticsEvent.Purchase, { transaction_id: transactionId, value, currency, payment_type: paymentType });
}

// ---- Engagement -----------------------------------------------------------

export function trackSearch(term: string): void {
  const t = term.trim();
  if (t.length < 2) return; // ignore noise / single keystrokes
  void logAnalyticsEvent(AnalyticsEvent.Search, { search_term: t });
}

export function trackSupportTicketCreate(priority: string, serviceId?: string | number): void {
  void logAnalyticsEvent(AnalyticsEvent.SupportTicketCreate, { priority, service_id: serviceId });
}

export function trackAskAiOpen(): void {
  void logAnalyticsEvent(AnalyticsEvent.AskAiOpen);
}

// ---- Service-console feature engagement -----------------------------------

/** Action verbs for `trackFeatureAction`. */
export type FeatureActionVerb = 'create' | 'edit' | 'delete' | 'enable' | 'disable' | 'toggle';

/**
 * Generic feature-interaction event for the service consoles (FastChannel/AES,
 * WAAP, WAF, Multi-CDN…). `service` = console codename (e.g. 'waap','cdn','waf',
 * 'fastchannel','aes'); `feature` = the feature area (e.g. 'custom_rule','channel');
 * `verb` = what happened. Feature *views* are already captured by `page_view` paths,
 * so this is for mutations (create/edit/delete/enable) that page_view can't see.
 */
export function trackFeatureAction(
  service: string,
  feature: string,
  verb: FeatureActionVerb,
  params?: Record<string, unknown>,
): void {
  void logAnalyticsEvent(AnalyticsEvent.FeatureAction, { service, feature, action: verb, ...params });
}
